import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Link, Text } from '@contrast-security-inc/design-system-typography'
import { FilterDefinition, FilterDefinitionAuto, FilterState, FiltersValues } from '../../../types'
import { FILTER_TYPES } from './constants'
import { AttributeFilters, FilterAttribute } from './types/attribute'
import { FilterSelection, SelectionFilters } from './types/selection'
import { FilterKeyValue, KeyValueFilters } from './types/key-value'
import { DateFilters, FilterDate } from './types/date'
import { AccordionWrapper } from './filter-accordion-wrapper'

export interface FilterProps {
  filterDef: FilterDefinitionAuto
  filters: FiltersValues
  updateFilter: (filterKey: string, filterState: FilterState | FilterState[]) => void
  resetFilter: (filterKey: string) => void
}

const filtersContainerCss = css`
  display: flex;
  flex-direction: column;
  padding-left: 16px;
  padding-right: 16px;
`
const filtersContainerHeaderCss = css`
  display: flex;
  height: 80px;
  margin: 4px 0px;
  align-items: center;
  justify-content: space-between;
`

export interface UpdateFilterOptions {
  clearExistingFilters?: boolean
  replaceUrlHistory?: boolean
}
export interface FiltersProps {
  // option props
  header?: React.ReactNode
  // filter props
  filterDefs: FilterDefinition[]
  filters: FiltersValues
  updateFilter: (
    filterKey: string,
    filterState: FilterState | FilterState[],
    options?: UpdateFilterOptions
  ) => void
  hasFilters: () => boolean
  resetAll: () => void
  resetFilter: (filterKey: string) => void
}

export const EmptyFilterComponent = () => (
  <Text>{useIntl().formatMessage({ id: 'detail_emptyFilterMessage' })}</Text>
)

export const Filters = (filtersProps: FiltersProps) => {
  const { header, resetAll, hasFilters, filterDefs, filters, resetFilter } = filtersProps
  const intl = useIntl()

  const visibleFilters = filterDefs
    .filter((fd) => !fd.renderManual)
    .map((fd) => {
      return fd as FilterDefinitionAuto
    })

  return (
    <div className={filtersContainerCss}>
      <div className={filtersContainerHeaderCss}>
        <Text font="titleH1">{intl.formatMessage({ id: 'title_filters' })}</Text>
        {hasFilters() && (
          <Link onClick={resetAll}>{intl.formatMessage({ id: 'action_resetAll' })}</Link>
        )}
      </div>
      {header}
      {visibleFilters.map((filterDef) => {
        const showAccordion = filterDef.filterType !== FILTER_TYPES.DATE

        return (
          <div key={filterDef.filterKey}>
            {showAccordion ? (
              <AccordionWrapper filterDef={filterDef} filters={filters} resetFilter={resetFilter}>
                {getFilterComponent(filterDef, filtersProps)}
              </AccordionWrapper>
            ) : (
              getFilterComponent(filterDef, filtersProps)
            )}
          </div>
        )
      })}
    </div>
  )
}

const getFilterComponent = (filterDef: FilterDefinitionAuto, filterProps: FiltersProps) => {
  switch (filterDef.filterType) {
    case 'attribute':
      return (
        <FilterAttribute
          filterDef={filterDef}
          filters={filterProps.filters as AttributeFilters}
          updateFilter={filterProps.updateFilter}
        />
      )
    case 'keyvalue':
      return (
        <FilterKeyValue
          filterDef={filterDef}
          filters={filterProps.filters as KeyValueFilters}
          updateFilter={filterProps.updateFilter}
        />
      )
    case 'selection':
      return (
        <FilterSelection
          filterDef={filterDef}
          filters={filterProps.filters as SelectionFilters}
          updateFilter={filterProps.updateFilter}
        />
      )
    case 'date':
      return (
        <FilterDate
          filterDef={filterDef}
          filters={filterProps.filters as DateFilters}
          updateFilter={filterProps.updateFilter}
        />
      )
    default:
      return <></>
  }
}
